import * as React from 'react'
import { Button } from '~/client/components/ui/button'
import { Skeleton } from '~/client/components/ui/skeleton'
import { Plus, CircleCheck } from 'lucide-react'
import { useShareableLists } from '~/client/shareable-list/use-shareable-lists.hook'
import { useAddSpaceToShareableList } from '~/client/shareable-list/use-add-space-to-shareable-list.hook'
import { toast } from 'sonner'
import type { ShareableListSummary } from '~/server/shareable-list/shareable-list.service'

type Props = {
  spaceId?: number
  onCreateNew: () => void
  onDone: () => void
}

export function SelectShareableListStep({ spaceId, onCreateNew, onDone }: Props) {
  const { data: lists, isLoading } = useShareableLists()
  const addMutation = useAddSpaceToShareableList()
  const [selectedId, setSelectedId] = React.useState<number | null>(null)

  const handleSelect = async (list: ShareableListSummary) => {
    if (typeof spaceId !== 'number') return
    setSelectedId(list.id)
    try {
      const result = await addMutation.mutateAsync({ listId: list.id, spaceId })
      if (result.alreadyExists) {
        toast('Space is already in this list', {
          description: list.name,
          position: 'bottom-right',
        })
        return
      }
      toast.success('Space added to collection', {
        description: list.name,
        position: 'bottom-right',
      })
      onDone()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add space to collection'
      toast.error(message, { position: 'bottom-right', richColors: true })
    }
  }

  const isSpaceInList = (list: ShareableListSummary) =>
    list.memberships?.some((m: any) => m.space_id === spaceId)

  return (
    <div className="space-y-6">
      <div className="rounded-[12px] border border-neutral-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] overflow-hidden bg-white max-h-[400px] overflow-y-auto">
        {isLoading ? (
          Array.from({ length: 3 }).map((_, idx) => (
            <React.Fragment key={`skeleton-${idx}`}>
              <div className="px-4 py-[18px]">
                <Skeleton className="h-4 w-3/4" />
              </div>
              {idx < 2 ? <div className="h-px bg-[#e4e1e9]" /> : null}
            </React.Fragment>
          ))
        ) : lists.length > 0 ? (
          lists.map((list, idx) => {
            const isSelected = selectedId === list.id
            const alreadyHasSpace = isSpaceInList(list)
            return (
              <React.Fragment key={list.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(list)}
                  disabled={addMutation.isPending && selectedId === list.id}
                  className={`w-full text-left px-4 py-[18px] flex items-center justify-between transition-colors ${alreadyHasSpace
                    ? 'bg-[rgba(138,61,255,0.02)]'
                    : isSelected
                      ? 'bg-[rgba(138,61,255,0.12)]'
                      : 'bg-white'
                    } hover:bg-[rgba(138,61,255,0.06)]`}
                >
                  <div className="flex-1">
                    <div className="text-[14px] font-medium text-[#55534e] leading-[14px]">
                      {list.name}
                    </div>
                    <div className="mt-1 text-[13px] text-[#9b9b9a]">
                      {list.memberships?.length ?? 0} spaces
                    </div>
                  </div>
                  {alreadyHasSpace && <CircleCheck className="size-5 text-[#8a3dff] shrink-0" />}
                </button>
                {idx < lists.length - 1 ? <div className="h-px bg-[#e4e1e9]" /> : null}
              </React.Fragment>
            )
          })
        ) : (
          <div className="flex flex-col items-center py-8 px-6">
            <div className="flex items-center justify-center size-14 rounded-full bg-[#f4f0fa] mb-4">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="#8a3dff" strokeWidth="1.5" />
                <rect x="14" y="3" width="7" height="9" rx="1.5" stroke="#8a3dff" strokeWidth="1.5" />
                <rect x="3" y="15" width="18" height="6" rx="1.5" stroke="#8a3dff" strokeWidth="1.5" />
              </svg>
            </div>
            <div className="text-[15px] font-semibold text-[#18003d] mb-1">Build your first Collection</div>
            <p className="text-[13px] text-[#95919a] text-center leading-[1.4] max-w-[260px]">
              Create a curated list of venues to share with colleagues or reuse for future recommendations.
            </p>
          </div>
        )}
      </div>

      <div className="mt-6 mb-3 w-full flex items-center justify-center gap-3">
        <div className="h-px w-24 bg-[#e4e1e9]" />
        <span className="text-[12px] text-black">or</span>
        <div className="h-px w-24 bg-[#e4e1e9]" />
      </div>

      <Button variant="revel" className="w-full h-11 rounded-[12px]" onClick={onCreateNew}>
        Create New Collection
        <Plus className="size-4" />
      </Button>
    </div>
  )
}
